import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Save, LogOut, Plus, Trash2, Loader2, ChevronDown, History } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { API, useContent } from "@/context/ContentContext";

const inputCls =
  "w-full rounded-xl border border-slate-900/10 bg-surface px-4 py-3 text-sm text-slate-800 placeholder:text-slate-500 transition-colors focus:border-electric/60 focus:outline-none focus:ring-1 focus:ring-electric/40";
const labelCls = "mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500";
const cardCls = "glass rounded-2xl p-6";
const TABS = ["Contact & Brand", "Plans", "Testimonials", "FAQ", "Policies", "Orders", "Messages"];
const STATUS_STYLES = {
  new: "border-amber-300/60 bg-amber-50 text-amber-700",
  paid: "border-blue-300/60 bg-blue-50 text-blue-700",
  delivered: "border-emerald-300/60 bg-emerald-50 text-emerald-700",
};

const fmtErr = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").filter(Boolean).join(", ") : "Something went wrong";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data.user);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="hero-bg flex min-h-screen items-center justify-center pt-[72px]">
      <form onSubmit={submit} className="glass w-full max-w-md rounded-3xl p-10" data-testid="admin-login-form">
        <LogoMark className="mx-auto h-14 w-14" />
        <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-slate-900">Admin Settings</h1>
        <p className="mt-2 text-center text-sm text-slate-500">Sign in to edit website content</p>
        <div className="mt-8 space-y-4">
          <input data-testid="admin-email-input" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password-input" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={busy} data-testid="admin-login-button" className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
        </div>
      </form>
    </main>
  );
}

export default function Admin() {
  const { reload } = useContent();
  const [user, setUser] = useState(undefined);
  const [draft, setDraft] = useState(null);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    document.title = "Admin — IPTV HUB";
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/content`).then(({ data }) => setDraft(JSON.parse(JSON.stringify(data.content))));
    axios.get(`${API}/admin/orders`, { withCredentials: true }).then(({ data }) => setOrders(data.orders || []));
    axios.get(`${API}/admin/messages`, { withCredentials: true }).then(({ data }) => setMessages(data.messages || []));
  }, [user]);

  if (user === undefined)
    return (
      <main className="flex min-h-screen items-center justify-center pt-[72px]">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </main>
    );
  if (!user) return <Login onLogin={setUser} />;

  const setS = (k, v) => setDraft((d) => ({ ...d, settings: { ...d.settings, [k]: v } }));
  const updPlan = (i, k, v) => setDraft((d) => ({ ...d, plans: d.plans.map((p, x) => (x === i ? { ...p, [k]: v } : p)) }));
  const updT = (i, k, v) => setDraft((d) => ({ ...d, testimonials: d.testimonials.map((t, x) => (x === i ? { ...t, [k]: v } : t)) }));
  const updF = (i, k, v) => setDraft((d) => ({ ...d, faqs: d.faqs.map((f, x) => (x === i ? { ...f, [k]: v } : f)) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, { content: draft }, { withCredentials: true });
      toast.success("Content saved — the website is updated.");
      reload();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const setOrderStatus = async (id, status) => {
    try {
      const { data } = await axios.put(`${API}/admin/orders/${id}`, { status }, { withCredentials: true });
      setOrders((os) =>
        os.map((o) => (o.id === id ? { ...o, status, status_history: data.status_history || o.status_history } : o))
      );
      toast.success(`Order ${id} marked ${status}`);
    } catch {
      toast.error("Could not update order status");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  return (
    <main className="min-h-screen pt-[72px] pb-24">
      <div className="mx-auto max-w-6xl px-4 pt-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-electric">Admin</p>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Website Content Settings</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving || !draft} data-testid="admin-save-button" className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mb-8 flex gap-2 overflow-x-auto pb-2" data-testid="admin-tabs">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === i ? "bg-electric text-slate-900 shadow-[0_0_18px_rgba(0,240,255,0.35)]" : "border border-slate-900/10 bg-slate-900/[0.04] text-slate-500 hover:text-slate-900"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {!draft ? (
          <Loader2 className="mx-auto mt-20 h-8 w-8 animate-spin text-electric" />
        ) : (
          <>
            {tab === 0 && (
              <div className={cardCls} data-testid="admin-contact-tab">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label><span className={labelCls}>WhatsApp number (display)</span><input data-testid="admin-whatsapp-display-input" className={inputCls} value={draft.settings.whatsapp_display} onChange={(e) => setS("whatsapp_display", e.target.value)} /></label>
                  <label><span className={labelCls}>WhatsApp number (international, digits only)</span><input data-testid="admin-whatsapp-intl-input" className={inputCls} value={draft.settings.whatsapp_intl} onChange={(e) => setS("whatsapp_intl", e.target.value.replace(/\D/g, ""))} /></label>
                  <label><span className={labelCls}>Contact email</span><input data-testid="admin-email-input-field" className={inputCls} value={draft.settings.email} onChange={(e) => setS("email", e.target.value)} /></label>
                  <label><span className={labelCls}>Policies last updated</span><input data-testid="admin-policies-updated-input" className={inputCls} value={draft.settings.policies_updated} onChange={(e) => setS("policies_updated", e.target.value)} /></label>
                  <label className="sm:col-span-2"><span className={labelCls}>Brand description (footer)</span><textarea rows={2} data-testid="admin-brand-description-input" className={`${inputCls} resize-none`} value={draft.settings.brand_description} onChange={(e) => setS("brand_description", e.target.value)} /></label>
                  <label className="sm:col-span-2"><span className={labelCls}>Hero subheading</span><textarea rows={2} data-testid="admin-hero-sub-input" className={`${inputCls} resize-none`} value={draft.settings.hero_sub} onChange={(e) => setS("hero_sub", e.target.value)} /></label>
                  <label className="sm:col-span-2"><span className={labelCls}>Order confirmation note</span><textarea rows={2} data-testid="admin-order-note-input" className={`${inputCls} resize-none`} value={draft.settings.order_success_note} onChange={(e) => setS("order_success_note", e.target.value)} /></label>
                  <label className="sm:col-span-2"><span className={labelCls}>Payment methods (comma separated)</span><input data-testid="admin-payment-methods-input" className={inputCls} value={(draft.settings.payment_methods || []).join(", ")} onChange={(e) => setS("payment_methods", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></label>
                </div>
              </div>
            )}

            {tab === 1 && (
              <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-plans-tab">
                {draft.plans.map((p, i) => (
                  <div key={p.id} className={cardCls} data-testid={`admin-plan-card-${i + 1}`}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label><span className={labelCls}>Plan name</span><input data-testid={`admin-plan-${p.id}-name-input`} className={inputCls} value={p.name} onChange={(e) => updPlan(i, "name", e.target.value)} /></label>
                      <label><span className={labelCls}>Price ($)</span><input type="number" step="0.01" data-testid={`admin-plan-${p.id}-price-input`} className={inputCls} value={p.price} onChange={(e) => updPlan(i, "price", parseFloat(e.target.value) || 0)} /></label>
                      <label><span className={labelCls}>Badge (optional)</span><input data-testid={`admin-plan-${p.id}-badge-input`} className={inputCls} value={p.badge || ""} onChange={(e) => updPlan(i, "badge", e.target.value)} /></label>
                      <label className="flex items-end gap-3 pb-1">
                        <input type="checkbox" data-testid={`admin-plan-${p.id}-popular-checkbox`} checked={!!p.popular} onChange={(e) => updPlan(i, "popular", e.target.checked)} className="h-5 w-5 accent-cyan-400" />
                        <span className="text-sm text-slate-600">Highlight as featured</span>
                      </label>
                      <label className="sm:col-span-2"><span className={labelCls}>Features (one per line)</span><textarea rows={4} data-testid={`admin-plan-${p.id}-features-input`} className={`${inputCls} resize-none`} value={(p.features || []).join("\n")} onChange={(e) => updPlan(i, "features", e.target.value.split("\n"))} /></label>
                      <label className="sm:col-span-2"><span className={labelCls}>Supported devices</span><input data-testid={`admin-plan-${p.id}-devices-input`} className={inputCls} value={p.devices} onChange={(e) => updPlan(i, "devices", e.target.value)} /></label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 2 && (
              <div className="space-y-6" data-testid="admin-testimonials-tab">
                {draft.testimonials.map((t, i) => (
                  <div key={i} className={cardCls} data-testid={`admin-testimonial-${i + 1}`}>
                    <div className="grid gap-4 sm:grid-cols-4">
                      <label><span className={labelCls}>Name</span><input data-testid={`admin-testimonial-${i + 1}-name-input`} className={inputCls} value={t.name} onChange={(e) => updT(i, "name", e.target.value)} /></label>
                      <label><span className={labelCls}>Role / device</span><input data-testid={`admin-testimonial-${i + 1}-role-input`} className={inputCls} value={t.role} onChange={(e) => updT(i, "role", e.target.value)} /></label>
                      <label><span className={labelCls}>Rating (1-5)</span><input type="number" min="1" max="5" data-testid={`admin-testimonial-${i + 1}-rating-input`} className={inputCls} value={t.rating} onChange={(e) => updT(i, "rating", parseInt(e.target.value) || 5)} /></label>
                      <div className="flex items-end pb-1">
                        <button
                          data-testid={`admin-testimonial-${i + 1}-remove-button`}
                          onClick={() => setDraft((d) => ({ ...d, testimonials: d.testimonials.filter((_, x) => x !== i) }))}
                          className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 transition-colors hover:bg-red-500/20"
                        >
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      </div>
                      <label className="sm:col-span-4"><span className={labelCls}>Review text</span><textarea rows={2} data-testid={`admin-testimonial-${i + 1}-text-input`} className={`${inputCls} resize-none`} value={t.text} onChange={(e) => updT(i, "text", e.target.value)} /></label>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="admin-testimonial-add-button"
                  onClick={() => setDraft((d) => ({ ...d, testimonials: [...d.testimonials, { name: "New Customer", role: "Customer", rating: 5, text: "Great service!" }] }))}
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm"
                >
                  <Plus className="h-4 w-4" /> Add testimonial
                </button>
              </div>
            )}

            {tab === 3 && (
              <div className="space-y-6" data-testid="admin-faq-tab">
                {draft.faqs.map((f, i) => (
                  <div key={i} className={cardCls} data-testid={`admin-faq-${i + 1}`}>
                    <div className="space-y-4">
                      <label><span className={labelCls}>Question {i + 1}</span><input data-testid={`admin-faq-${i + 1}-q-input`} className={inputCls} value={f.q} onChange={(e) => updF(i, "q", e.target.value)} /></label>
                      <label><span className={labelCls}>Answer</span><textarea rows={2} data-testid={`admin-faq-${i + 1}-a-input`} className={`${inputCls} resize-none`} value={f.a} onChange={(e) => updF(i, "a", e.target.value)} /></label>
                      <button
                        data-testid={`admin-faq-${i + 1}-remove-button`}
                        onClick={() => setDraft((d) => ({ ...d, faqs: d.faqs.filter((_, x) => x !== i) }))}
                        className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300 transition-colors hover:bg-red-500/20"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="admin-faq-add-button"
                  onClick={() => setDraft((d) => ({ ...d, faqs: [...d.faqs, { q: "New question?", a: "The answer." }] }))}
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm"
                >
                  <Plus className="h-4 w-4" /> Add FAQ
                </button>
              </div>
            )}

            {tab === 4 && (
              <div className="space-y-6" data-testid="admin-policies-tab">
                {["privacy", "terms", "refund"].map((kind) => (
                  <div key={kind} className={cardCls} data-testid={`admin-policy-${kind}`}>
                    <label className="block">
                      <span className={labelCls}>{draft.policies[kind].title} — start sections with "## Heading" on its own line</span>
                      <textarea
                        rows={12}
                        data-testid={`admin-policy-${kind}-body-input`}
                        className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
                        value={draft.policies[kind].body}
                        onChange={(e) => setDraft((d) => ({ ...d, policies: { ...d.policies, [kind]: { ...d.policies[kind], body: e.target.value } } }))}
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}

            {tab === 5 && (
              <div className={cardCls} data-testid="admin-orders-tab">
                {orders.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-500">No orders yet — they will appear here as customers check out.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[880px] text-left text-sm" data-testid="admin-orders-table">
                      <thead>
                        <tr className="border-b border-slate-900/10 text-xs uppercase tracking-wider text-slate-500">
                          {["Order ID", "Status", "Plan", "Price", "Name", "WhatsApp", "Email", "Device", "Payment", "Date"].map((h) => (
                            <th key={h} className="px-3 py-3 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o) => (
                          <Fragment key={o.id}>
                            <tr className="border-b border-slate-900/10 text-slate-600" data-testid={`admin-order-row-${o.id}`}>
                              <td className="px-3 py-3">
                                <button
                                  onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                                  data-testid={`admin-order-${o.id}-toggle-history`}
                                  className="inline-flex items-center gap-1.5 font-mono text-electric transition-colors hover:underline"
                                >
                                  {o.id}
                                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === o.id ? "rotate-180" : ""}`} />
                                </button>
                              </td>
                              <td className="px-3 py-3">
                                <select
                                  data-testid={`admin-order-${o.id}-status-select`}
                                  value={o.status || "new"}
                                  onChange={(e) => setOrderStatus(o.id, e.target.value)}
                                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${STATUS_STYLES[o.status] || STATUS_STYLES.new}`}
                                >
                                  <option value="new">New</option>
                                  <option value="paid">Paid</option>
                                  <option value="delivered">Delivered</option>
                                </select>
                              </td>
                              <td className="px-3 py-3">{o.plan_name}</td>
                              <td className="px-3 py-3">${Number(o.price).toFixed(2)}</td>
                              <td className="px-3 py-3">{o.name}</td>
                              <td className="px-3 py-3">{o.whatsapp}</td>
                              <td className="px-3 py-3">{o.email}</td>
                              <td className="px-3 py-3">{o.device}</td>
                              <td className="px-3 py-3">{o.payment_method}</td>
                              <td className="px-3 py-3 text-xs text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                            </tr>
                            {expanded === o.id && (
                              <tr>
                                <td colSpan={10} className="border-b border-slate-900/10 bg-slate-50/80 px-3 py-4">
                                  <div className="flex items-start gap-2" data-testid={`admin-order-${o.id}-history`}>
                                    <History className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                                    <div className="flex flex-col gap-1.5">
                                      {(o.status_history || []).map((h, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs">
                                          <span
                                            className={`h-2 w-2 shrink-0 rounded-full ${
                                              h.status === "new" ? "bg-amber-400" : h.status === "paid" ? "bg-blue-500" : "bg-emerald-500"
                                            }`}
                                          />
                                          <span className="font-semibold capitalize text-slate-700">{h.status}</span>
                                          <span className="text-slate-400">{dayjs(h.at).format("DD MMM YYYY, HH:mm")}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 6 && (
              <div className={cardCls} data-testid="admin-messages-tab">
                {messages.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-500">No contact messages yet.</p>
                ) : (
                  <div className="space-y-4">
                    {messages.map((m) => (
                      <div key={m.id} className="rounded-xl border border-slate-900/10 bg-slate-900/[0.03] p-5" data-testid={`admin-message-${m.id}`}>
                        <p className="text-sm font-semibold text-slate-900">{m.name} <span className="ml-2 font-normal text-slate-500">{m.email}</span></p>
                        <p className="mt-2 text-sm text-slate-500">{m.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
